/**
 * The API client.
 *
 * Everything goes through `request` so that two things stay in one place: the credentials mode
 * (the session is a cookie, so `same-origin` is required or nothing is authenticated), and the
 * distinction between "the server said no" and "the network is not there". The second one is
 * never an error the user sees — brief §14a.1 — so the caller has to be able to tell them
 * apart without parsing a message.
 */

export type ClientConfig = {
  build: string;
  map: { styleUrlLight: string; styleUrlDark: string; attribution: string };
  auth: { requireInvite: boolean };
  /** Null when this instance has no such build to hand out. */
  android: {
    apk: { version: string; size: number } | null;
    wearApk: { version: string; size: number } | null;
  };
};

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  constructor(code: string, status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

/** Thrown when the request never reached the server. Callers fall back to the cache. */
export class OfflineError extends Error {
  constructor() {
    super('offline');
    this.name = 'OfflineError';
  }
}

/**
 * The device token from pairing (brief §11.7), when there is one.
 *
 * Held in a module variable rather than read from storage on every call: this is on the path
 * of every request the app makes, and `Preferences.get` is a bridge round trip on Android.
 * `setDeviceToken` is called once at start-up and again when pairing completes.
 */
let deviceToken: string | undefined;

export function setDeviceToken(token: string | undefined): void {
  deviceToken = token;
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
        // A bearer request carries no cookie and is therefore exempt from the origin check on
        // the server — which is exactly right for the APK, where there is no browser origin
        // to check against.
        ...(deviceToken === undefined ? {} : { authorization: `Bearer ${deviceToken}` }),
        ...init.headers,
      },
    });
  } catch {
    // fetch only rejects when the request did not complete: DNS, TCP, TLS, or no network at
    // all. An HTTP error status resolves normally and is handled below.
    throw new OfflineError();
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new ApiError(
      body.error ?? 'internal_error',
      response.status,
      body.message ?? response.statusText,
    );
  }
  return (await response.json()) as T;
}

/**
 * A request for something that is not JSON — an avatar (amendment 21) — with the same
 * credentials as `request`. An `<img src>` would carry the cookie but never the APK's bearer
 * token, so the pictures are fetched here and shown as object URLs.
 */
export async function requestBlob(path: string): Promise<Blob> {
  let response: Response;
  try {
    response = await fetch(path, {
      credentials: 'same-origin',
      headers: deviceToken === undefined ? {} : { authorization: `Bearer ${deviceToken}` },
    });
  } catch {
    throw new OfflineError();
  }
  if (!response.ok) throw new ApiError('not_found', response.status, response.statusText);
  return await response.blob();
}

export const getClientConfig = () => request<ClientConfig>('/api/config');
